import clsx from 'clsx'
import { CONSTS } from 'config/objectConst'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
	durationParser,
	Param,
	setArray,
	type SimplifiedCampaign,
	URLSearchParser
} from 'shared/search-adapters'
import { typo } from 'shared/styles'
import { Button } from 'shared/ui'

import { isAddContactsModalOpen } from '../AddModal/util'
import { AccountStatus } from './AccountStatus'
import { Avatar } from './Avatar'
import { CampaignsSelect } from './CampaignsSelect.tsx'
import { Connection } from './Connection'
import { Dates } from './DatePickerRange.tsx'
import css from './Filters.module.css'
import { Locations } from './Locations.tsx'
import { NotCreated } from './NotCreated'
import { NotQualificationsSelector } from './NotQualificationsSelector'
import { QualificationsSelector } from './QualificationsSelector'
import { closeFilter } from './store'
export function ContactsForm() {
	const [params, setParams] = useSearchParams()

	const [contacted, setContacted] = Dates.useDates(Param.LastContacted),
		[created, setCreated] = Dates.useDates(Param.Created),
		[notContacted, setNotContacted] = Dates.useDates(Param.NotContacted),
		[notCreated, setNotCreated] = Dates.useDates(Param.NotCreated)

	const [avatar, setAvatar] = useState(() => params.get(Param.Avatar) || '0'),
		[campaigns, setCampaigns] = useState(() =>
			CampaignsSelect.init(params, Param.Campaigns)
		),
		[distance, setDistance] = useState(() =>
			URLSearchParser.LinkedInCircle(params)
		),
		[locations, setLocations] = useState(() =>
			Locations.init(params, Param.Locations)
		),
		[notAvatar, setNotAvatar] = useState(() => params.get(Param.NotAvatar) || '0'),
		[notCampaigns, setNotCampaigns] = useState(() =>
			CampaignsSelect.init(params, Param.NotCampaigns)
		),
		[notQualifications, setNotQualifications] =
			NotQualificationsSelector.useState(params),
		[qualifications, setQualifications] =
			QualificationsSelector.useState(params)

	return (
		<form
			className={css.form}
			onReset={event => {
				const status = event.currentTarget.status as RadioNodeList
				status.value = '0'

				setCampaigns([])
				setNotCampaigns([])
				setContacted(null)
				setCreated(null)
				setLocations([])
				setDistance([])
				setNotContacted(null)
				setNotCreated(null)
				setQualifications([])
				setNotQualifications([])
				setAvatar('0')
				setNotAvatar('0')
			}}
			onSubmit={event => {
				event.preventDefault()
				event.stopPropagation()
				const status = event.currentTarget.status as RadioNodeList
				const avatarRadio = event.currentTarget.avatar as RadioNodeList
				const notAvatarRadio = event.currentTarget.notAvatar as RadioNodeList

				setParams(prev => {
					setArray(
						prev,
						Param.Distance,
						distance.map(item => item.value)
					)
					params.delete(Param.Skip)
					params.delete(Param.ModalSkip)

					setArray(params, Param.Locations, locations.map(Locations.parse))

					if (status.value === '1') {
						params.set(Param.Premium, '1')
					} else {
						params.delete(Param.Premium)
					}

					setUrlCampaign(prev, campaigns, Param.Campaigns)
					setUrlCampaign(prev, notCampaigns, Param.NotCampaigns)
					durationParser.serialize(created?.value ?? null, prev, Param.Created)
					durationParser.serialize(
						contacted?.value ?? null,
						prev,
						Param.LastContacted
					)
					durationParser.serialize(
						notContacted?.value ?? null,
						prev,
						Param.NotContacted
					)

					setArray(
						prev,
						isAddContactsModalOpen()
							? Param.ModalLinkedInQualification
							: Param.LinkedInQualification,
						qualifications.map(item => item.value)
					)

					setArray(
						prev,
						Param.NotLinkedInQualification,
						notQualifications.map(item => item.value)
					)

					durationParser.serialize(
						notCreated?.value ?? null,
						prev,
						Param.NotCreated
					)

					const avatarValue = avatarRadio?.value || '0'
					const notAvatarValue = notAvatarRadio?.value || '0'

					if (avatarValue !== '0') {
						prev.set(Param.Avatar, avatarValue)
					} else {
						prev.delete(Param.Avatar)
					}

					if (notAvatarValue !== '0') {
						prev.set(Param.NotAvatar, notAvatarValue)
					} else {
						prev.delete(Param.NotAvatar)
					}

					return prev
				})

				closeFilter()
			}}
		>
			<section className={css.section}>
				<h4 className={clsx(typo.subSection, css.title)}>Include</h4>
				<Connection setter={setDistance} value={distance} />
				<Locations locations={locations} setLocations={setLocations} />
				<Dates
					dates={contacted}
					label='Last Contacted'
					placeholder='Select period'
					setDates={setContacted}
				/>

				<CampaignsSelect
					className={css.start}
					setter={setCampaigns}
					value={campaigns}
				/>

				<QualificationsSelector
					currents={qualifications}
					setter={setQualifications}
				/>

				<Dates
					dates={created}
					label={`Added to ${CONSTS.PROJECT_NAME}`}
					placeholder='Select date'
					setDates={setCreated}
				/>



				<AccountStatus initial={Boolean(params.get(Param.Premium))} />

				<Avatar initial={avatar} name="avatar" />
			</section>

			<section className={clsx(css.section, css.accentSection)}>
				<h4 className={clsx(typo.subSection, css.title)}>Exclude</h4>

				<CampaignsSelect
					className={css.start}
					setter={setNotCampaigns}
					value={notCampaigns}
				/>

				<NotQualificationsSelector
					currents={notQualifications}
					setter={setNotQualifications}
				/>

				<NotCreated dates={notCreated} setDates={setNotCreated} />

				<Avatar className={css.start} initial={notAvatar} name="notAvatar"/>


				<Dates
					dates={notContacted}
					label='Last Contacted '
					placeholder='Select period'
					setDates={setNotContacted}
				/>

			</section>

			<div className={css.footer}>
				<Button type='reset' variant='secondary'>
					Clear all
				</Button>

				<Button type='submit'>Apply</Button>
			</div>
		</form>
	)
}

function setUrlCampaign(
	params: URLSearchParams,
	campaigns: SimplifiedCampaign[],
	key: Param
) {
	const simplified = CampaignsSelect.simplify(campaigns)

	if (simplified.length) {
		params.set(key, JSON.stringify(simplified))
	} else {
		params.delete(key)
	}
}
